<script lang="ts" setup>
// @ts-ignore
import { Splide, SplideSlide } from '@splidejs/vue-splide'
import { Options } from '@splidejs/splide'
import { AutoScroll } from '@splidejs/splide-extension-auto-scroll'

export interface WidgetFlashNewsProps {
    data: FlashNewsArticle[];
    title: string;
    url: string;
    headingLevel: string;
}
const props = defineProps<WidgetFlashNewsProps>()
const splideRef = ref<Splide>()
const widgetRef = ref<HTMLElement>()
const isPaused = ref(false)

useFixSlideRoles(splideRef)

const observer = useObserver({ rootMargin: '0px', threshold: 0.3 })

// Pause auto-scroll (WCAG 2.2.2)
const pauseAutoScroll = (): void => {
    const autoScroll = splideRef.value?.splide?.Components.AutoScroll
    if (autoScroll) {
        autoScroll.pause()
        isPaused.value = true
    }
}

// Resume auto-scroll
const playAutoScroll = (): void => {
    const autoScroll = splideRef.value?.splide?.Components.AutoScroll
    if (autoScroll) {
        autoScroll.play()
        isPaused.value = false
    }
}

// Stop auto-scroll when user interacts with arrow keys (WCAG 2.2.2)
const handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' ||
        e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        pauseAutoScroll()
    }
}

// Stop auto-scroll when focus enters from outside the widget (WCAG 2.2.2)
const handleFocusIn = (e: FocusEvent): void => {
    const root = e.currentTarget as HTMLElement
    const from = e.relatedTarget as Node | null
    if (from && root.contains(from)) {
        return
    }
    pauseAutoScroll()
}

// Resume only when focus leaves the widget entirely (focusout bubbles from descendants)
const handleFocusOut = (e: FocusEvent): void => {
    const root = e.currentTarget as HTMLElement
    const to = e.relatedTarget as Node | null
    if (to && root.contains(to)) {
        return
    }
    playAutoScroll()
}

// Handle Splide mounted event to set up move listener (WCAG 2.2.2)
const handleSplideMounted = (splide: any): void => {
    // Pause auto-scroll when user navigates slides manually
    splide.on('move', () => {
        pauseAutoScroll()
    })
}

onMounted(() => {
    if (!widgetRef.value) return

    // Add keyboard handler to pause auto-scroll on arrow navigation
    // Use capture phase to intercept events before Splide handles them
    widgetRef.value.addEventListener('keydown', handleKeyDown, true)

    observer(widgetRef.value, (isIntersecting) => {
        const autoScroll = splideRef.value?.splide?.Components.AutoScroll
        if (autoScroll) {
            if (isIntersecting && !isPaused.value) {
                setTimeout(() => {
                    if (!isPaused.value) {
                        autoScroll.play()
                    }
                }, 2000)
            } else {
                autoScroll.pause()
            }
        }
    })
})

onUnmounted(() => {
    if (widgetRef.value) {
        widgetRef.value.removeEventListener('keydown', handleKeyDown, true)
    }
})

const sliderOptions = reactive<Options>({
    type: 'loop',
    autoWidth: true,
    pagination: false,
    arrows: false,
    slideFocus: false,
    focusableNodes: '',
    gap: '4rem',
    padding: { left: '4rem', right: 0 }, 
    autoScroll: {
        speed: 0.7,
        pauseOnHover: true,
        pauseOnFocus: true,
        autoStart: false,
    },
    aria: {
        slide: false,
        container: false,
    },
})
</script>
<template>
  <section
    ref="widgetRef"
    class="widget-flash-news rounded bg-secondary overflow-hidden"
    data-cy="flash_news"
    :aria-label="props.title"
    @focusin.capture="handleFocusIn"
    @focusout.capture="handleFocusOut"
  >
    <Tracker :data="{ position: props.title }">
      <div class="widget-flash-news-header d-flex justify-content-between align-items-center p-4 p-md-6">
        <VNuxtLink
          :to="props.url"
          class="text-white mb-0 delta"
        >
          <Heading
            level="h2"
            class="text-white mb-0 delta text-nowrap text-decoration-underline"
          >
            {{ props.title }}
          </Heading>
        </VNuxtLink>
      </div>
      <div
        class="widget-flash-news-content is-animated bg-white m-1 overflow-hidden"
      >
        <Splide
          ref="splideRef"
          :options="sliderOptions"
          :extensions="{ AutoScroll }"
          class="px-0 py-4 py-md-5"
          @splide:mounted="handleSplideMounted"
        >
          <SplideSlide
            v-for="(article, index) in props.data"
            :key="`fna-${index}`"
          >
            <Tracker :data="{ title: article.title }">
              <FlashNewsArticle
                class="flex-shrink-0"
                heading-level="h3"
                :article="article"
              />
            </Tracker>
          </SplideSlide>
        </Splide>
      </div>
    </Tracker>
  </section>
</template>
<style lang="scss" scoped>
@import "./WidgetFlashNews.scss";
</style>
